import {
    MAX_PARAMETER_DISPLAY_NAME_LENGTH,
    MIN_PARAMETER_DISPLAY_NAME_LENGTH,
    PARAMETER_DISPLAY_NAME_REGEX
} from 'Utils';
import { getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

export const displayNameSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const parameterDisplayName = t('Display Name');
    return Yup.string()
        .required(validationT('required', { field: parameterDisplayName }))
        .strict()
        .trim(validationT('noLeadingTrailingSpaces'))
        .matches(PARAMETER_DISPLAY_NAME_REGEX, t('invalidParameterDisplayName'))
        .min(MIN_PARAMETER_DISPLAY_NAME_LENGTH, validationT('minLength', { field: parameterDisplayName, charCount: 1 }))
        .max(
            MAX_PARAMETER_DISPLAY_NAME_LENGTH,
            validationT('maxLength', { field: parameterDisplayName, charCount: 32 })
        );
};

export const descriptionSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const parameterDescription = t('Parameter Description');
    return Yup.string()
        .required(validationT('required', { field: parameterDescription }))
        .trim(validationT('noLeadingTrailingSpaces'))
        .strict()
        .min(1, validationT('minLength', { field: parameterDescription, charCount: 1 }))
        .max(150, validationT('maxLength', { field: parameterDescription, charCount: 150 }))
        .test({
            name: 'parameterDescription',
            exclusive: true,
            message: validationT('backslashOrSingleQuote'),
            // eslint-disable-next-line
            test: (value) => value?.match("[/\\\\']") === null
        });
};

export const velocityTypeSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const velocityType = t('Velocity Type');

    return Yup.string()
        .required(validationT('required', { field: velocityType }))
        .trim(validationT('noLeadingTrailingSpaces'));
};

const aggregationFieldSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const aggregationField = t('Aggregation Field');
    return Yup.lazy(() => {
        return Yup.array().when('velocityType', {
            is: (type: any) => type !== 'Count',
            then: Yup.array()
                .required(validationT('required', { field: aggregationField }))
                .min(1, validationT('minSelect', { field: aggregationField, minSelect: 1 })),
            otherwise: Yup.array().notRequired()
        });
    });
};

const indexesSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    const velocityIndex = t('Velocity Index');
    return Yup.array()
        .ensure()
        .required(validationT('required', { field: velocityIndex }))
        .min(1, validationT('minSelect', { field: velocityIndex, minSelect: 1 }));
};

const timePeriodSchema = (t: TFunction, validationT: (s: string, options?: any) => string) => {
    // max up to 7 days = 24h * 60 minutes * 60 seconds = 604,800 seconds
    const timePeriod = t('Time Period');
    const timePeriodDuration = t('Duration');
    const timePeriodUnit = t('Unit');

    return Yup.object({
        duration: Yup.number()
            .required(validationT('required', { field: timePeriodDuration }))
            .positive(validationT('positive', { field: timePeriodDuration }))
            .integer(validationT('integer', { field: timePeriodDuration }))
            .test({
                name: 'time period duration test against unit',
                test: (duration, ctx) => {
                    if (ctx.parent.unit[0] && duration !== undefined) {
                        switch (ctx.parent.unit[0]) {
                            case 'Days':
                                return duration > 7 ? ctx.createError({ message: `Max value is ${7} days` }) : true;
                            case 'Hours':
                                return duration > 7 * 24
                                    ? ctx.createError({ message: `Max value is ${7 * 24} hours` })
                                    : true;
                            case 'Minutes':
                                return duration > 7 * 24 * 60
                                    ? ctx.createError({ message: `Max value is ${7 * 24 * 60} minutes` })
                                    : true;
                            case 'Seconds':
                                return duration > 7 * 24 * 60 * 60
                                    ? ctx.createError({ message: `Max value is ${7 * 24 * 60 * 60} seconds` })
                                    : true;
                            default:
                                return false;
                        }
                    }

                    return true;
                }
            }),
        unit: Yup.array()
            .min(1, validationT('minSelect', { field: timePeriodUnit, minSelect: 1 }))
            .required(validationT('required', { field: timePeriodUnit }))
    }).required(validationT('required', { field: timePeriod }));
};

const filtersSchema = () => {
    return Yup.array()
        .of(
            Yup.object().shape({
                parameter: Yup.string(),
                type: Yup.string(),
                operator: Yup.string(),
                value: Yup.mixed().test('is-mixed', 'Field is not valid', function (val) {
                    return typeof val === 'number' || typeof val === 'string';
                })
            })
        )
        .notRequired();
};

export const addVelocityValidationSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.object({
        displayName: displayNameSchema(t, validationT),
        description: descriptionSchema(t, validationT),
        velocityType: velocityTypeSchema(t, validationT),
        aggregationField: aggregationFieldSchema(t, validationT),
        timePeriod: timePeriodSchema(t, validationT),
        indexes: indexesSchema(t, validationT),
        filters: filtersSchema()
    });
};
